import './DetailedFilter.css';

import { Dispatch, SetStateAction } from 'react';
import FilterItem from '../FilterItem/FilterItem';
import SelectFiltersPopover from '../../../SelectFiltersPopover/SelectFiltersPopover';
import { checkFilterOnSubmit } from '../../utils/checkFilterOnSubmit';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { ReferenceControl, SelectedFilter } from '../../../../../types/types';
import FilterHeader from '../FilterHeader/FilterHeader';
import SeptimLoadingSpinner from '../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import { shouldDropFilterValueOnMatchModeChange } from '../../../../../utils/filters/shouldDropFilterValueOnMatchModeChange';

interface DetailedFilterProps {
  submitFilters: (filters?: any[]) => void;
  filter: any;
  popoverFilters: any[];
  setPopoverFilters: Dispatch<SetStateAction<any[]>>;
  selectedFilters: SelectedFilter[];
  setSelectedFilters: Dispatch<SetStateAction<SelectedFilter[]>>;
  setDefaultPopoverFilters: (getFromActiveFilters?: boolean) => void;
  submitPopoverFilters: (
    filters: any,
    getValueFromActiveFilters?: boolean
  ) => void;
  isSlaveFilter?: boolean;
  dataset: any;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  openFullscreenReference: (
    name: string,
    datasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const DetailedFilter = ({
  filter,
  popoverFilters,
  selectedFilters,
  isSlaveFilter = false,
  dataset,
  referenceDatasets,
  submitFilters,
  setPopoverFilters,
  setSelectedFilters,
  setDefaultPopoverFilters,
  submitPopoverFilters,
  setReferenceDatasets,
  openFullscreenReference,
}: DetailedFilterProps) => {
  const { t } = useTranslate();

  const onSubmitFilters = () => {
    const checkedFilters = selectedFilters.map((filter) => {
      const error = checkFilterOnSubmit(
        filter.value,
        filter.matchMode,
        filter.error,
        t
      );
      return { ...filter, error };
    });

    setSelectedFilters(checkedFilters);
    const hasError = checkedFilters.some((f) => f.error !== null);

    if (!hasError) {
      submitFilters(checkedFilters);
    }
  };

  const resetFilters = () => {
    setDefaultPopoverFilters();
    setSelectedFilters([]);
    submitFilters([]);
  };

  const setFilterMatchMode = (name, datasetId, matchMode) => {
    setSelectedFilters((v) =>
      v.map((filter) => {
        if (filter.name !== name || filter.datasetId !== datasetId) {
          return { ...filter };
        }
        if (
          !shouldDropFilterValueOnMatchModeChange(
            filter.matchMode,
            matchMode,
            filter.value
          )
        ) {
          return { ...filter, matchMode };
        }
        // SelectedFilter.value is not nullable, so '' is this shape's empty value.
        return { ...filter, matchMode, value: '' };
      })
    );
  };

  const setFilterValue = (name, datasetId, value) => {
    setSelectedFilters((v) =>
      v.map((filter) => {
        if (filter.name === name && filter.datasetId === datasetId) {
          return { ...filter, value };
        }
        return { ...filter };
      })
    );
  };

  const setFilterError = (name, datasetId, error) => {
    setSelectedFilters((v) =>
      v.map((filter) => {
        if (filter.name === name && filter.datasetId === datasetId) {
          return { ...filter, error };
        }
        return { ...filter };
      })
    );
  };

  const setPopoverFilterValue = (value, datasetIndex, itemIndex) => {
    setPopoverFilters((v) => {
      const newValue = [...v];
      const newItems = [...v[datasetIndex].items];
      newItems[itemIndex].value = value;
      newValue[datasetIndex] = { ...v[datasetIndex], items: newItems };
      return newValue;
    });
  };

  const deleteFilter = (name, datasetId) => {
    setSelectedFilters((v) =>
      v.filter(
        (filter) => !(filter.name === name && filter.datasetId === datasetId)
      )
    );
    const datasetIndex = popoverFilters.findIndex((v) => v.id === datasetId);
    const itemIndex = popoverFilters[datasetIndex]?.items.findIndex(
      (v) => v.name === name
    );
    setPopoverFilterValue(false, datasetIndex, itemIndex);
  };

  if (!filter) {
    return (
      <div className="detailed-filter-loading">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <FilterHeader
        onSubmitFilters={onSubmitFilters}
        onResetFilters={resetFilters}
        dataset={dataset}
        caption={filter?.caption}
        selectedFilters={selectedFilters}
      />
      <div className="detailed-filters-section">
        <SelectFiltersPopover
          popoverFilters={popoverFilters}
          setPopoverFilters={setPopoverFilters}
          submitPopoverFilters={submitPopoverFilters}
          isSlaveFilter={isSlaveFilter}
          dataset={dataset}
        />
        <div className="detailed-filters-container">
          <div>
            {selectedFilters.map((item) => (
              <FilterItem
                key={`${item.datasetId}_${item.name}`}
                item={item}
                setFilterMatchMode={setFilterMatchMode}
                setFilterValue={setFilterValue}
                setFilterError={setFilterError}
                deleteFilter={deleteFilter}
                dataset={dataset}
                referenceDatasets={referenceDatasets}
                setReferenceDatasets={setReferenceDatasets}
                openFullscreenReference={openFullscreenReference}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailedFilter;
